/**
 * Enterprise edition feature-gate store.
 *
 * `enterpriseEdition` is a cluster-wide feature gate (`GET
 * /api/v1/operator/features`). While it is off the UI hides the enterprise
 * edition entirely without deleting code or i18n keys, so this is a
 * module-level store in the style of `src/i18n/store.ts`: components
 * subscribe with `useSyncExternalStore` and repaint when the gate flips.
 *
 * Only the Shell writes this store (it is the one place that calls
 * `useFeatureGates()`). Every other component only reads it, which keeps
 * components that tests render without an `AuthProvider` (Shell needs
 * `useAuth`) free of that dependency.
 */

import { useSyncExternalStore } from "react";

/** Name of the cluster feature gate that unlocks the enterprise edition. */
export const ENTERPRISE_EDITION_GATE = "enterpriseEdition";

let enabled = false;
const listeners = new Set<() => void>();

/** Whether the enterprise edition gate is currently enabled. Defaults to false. */
export function isEnterpriseEditionEnabled(): boolean {
  return enabled;
}

/** Set the gate state and notify subscribers. No-op when the value is unchanged. */
export function setEnterpriseEditionEnabled(value: boolean): void {
  if (value === enabled) return;
  enabled = value;
  for (const listener of listeners) listener();
}

/** Subscribe to gate changes. Returns the unsubscribe function. */
export function subscribeEnterpriseEdition(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** React hook: re-renders the component when the gate flips. */
export function useEnterpriseEdition(): boolean {
  return useSyncExternalStore(subscribeEnterpriseEdition, isEnterpriseEditionEnabled);
}
